import { axiosInstance } from '../utils/axiosApp';
import { isAxiosError } from 'axios';
import { AccessLevel } from '../utils/constants';

export const GroupStaffRole = {
  OWNER: 'owner',
  TEACHER: 'teacher',
  VIEWER: 'viewer',
} as const;

export type GroupStaffRole =
  (typeof GroupStaffRole)[keyof typeof GroupStaffRole];

export type StoredOrg = {
  org_id: string;
  name: string;
  logo_url?: string | null;
};

export type StoredGroup = {
  group_id: string;
  name: string;
  illustration?: string | null;
  illustration_url?: string | null;
  role?: GroupStaffRole | null;
  can_manage: boolean;
  org?: StoredOrg | null;
  grade?: string | null;
  country?: string | null;
  age_min?: number | null;
  age_max?: number | null;
  join_code?: string;
  join_code_active?: boolean;
};

export type CreateGroupPayload = {
  name: string;
  illustration?: string | null;
  org_id?: string;
  grade?: string | null;
  country?: string | null;
  age_min?: number | null;
  age_max?: number | null;
};
export type CreateGroupResponse = {
  group_id: string;
};

export type UpdateGroupPayload = {
  name?: string;
  illustration?: string | null;
  grade?: string | null;
  country?: string | null;
  age_min?: number | null;
  age_max?: number | null;
};

export type GroupMemberSummary = {
  profile_id: string;
  name?: string;
  avatar: string;
};

export type GroupStaffSummary = {
  account_id: string;
  name: string;
  email: string;
  role: GroupStaffRole;
  is_me?: boolean;
};

export type GroupStaffInvitation = {
  invitation_id: string;
  email: string;
  role: GroupStaffRole;
  status: 'pending';
};

export async function listGroupMembers(
  groupId: string
): Promise<GroupMemberSummary[]> {
  const res = await axiosInstance().get(`/api/groups/${groupId}/members`, {
    timeout: 15_000,
  });
  return Array.isArray(res.data) ? res.data : [];
}

// POST GroupMember
export type AddGroupMemberPayload = {
  profile_ids: string[];
  level?: AccessLevel;
};
export type GroupMemberResult = {
  profile_id: string;
  status: 'joined' | 'unconfirmed';
};
export type GroupMembersResponse = {
  results: GroupMemberResult[];
};

// These responses reject the selection before any membership write starts.
export function isGroupMembershipRejection(error: unknown): boolean {
  return (
    isAxiosError(error) &&
    [400, 401, 403, 404, 422].includes(error.response?.status ?? 0)
  );
}
export async function addGroupMember(
  groupId: string,
  payload: AddGroupMemberPayload
): Promise<GroupMembersResponse> {
  const res = await axiosInstance().post(
    `/api/groups/${groupId}/members`,
    { ...payload, level: payload.level ?? AccessLevel.STANDARD },
    { timeout: 15_000 }
  );
  return res.data;
}

export async function addGroupStaff(
  groupId: string,
  payload: { account_id?: string; email?: string; role: GroupStaffRole }
): Promise<{
  account_id: string | null;
  email?: string | null;
  role: GroupStaffRole;
  invited: boolean;
  email_queued: boolean | null;
}> {
  const res = await axiosInstance().post(
    `/api/groups/${groupId}/staff`,
    payload
  );
  return res.data;
}

export async function listMyGroups(): Promise<StoredGroup[]> {
  const res = await axiosInstance().get('/api/groups');
  return Array.isArray(res.data) ? res.data : [];
}

export async function getGroupById(groupId: string): Promise<StoredGroup> {
  const res = await axiosInstance().get(`/api/groups/${groupId}`);
  return res.data;
}

export async function createGroup(
  payload: CreateGroupPayload
): Promise<CreateGroupResponse> {
  const res = await axiosInstance().post('/api/groups', payload);
  return res.data;
}

export async function updateGroup(
  groupId: string,
  payload: UpdateGroupPayload,
  signal?: AbortSignal
): Promise<StoredGroup> {
  const res = await axiosInstance().patch(`/api/groups/${groupId}`, payload, {
    signal,
    timeout: 15_000,
  });
  return res.data;
}

export async function deleteGroup(groupId: string): Promise<void> {
  await axiosInstance().delete(`/api/groups/${groupId}`);
}

export async function leaveGroup(groupId: string): Promise<void> {
  await axiosInstance().post(`/api/groups/${groupId}/leave`);
}

export async function removeGroupMember(
  groupId: string,
  profileId: string
): Promise<void> {
  await axiosInstance().delete(`/api/groups/${groupId}/members/${profileId}`);
}

export async function generateJoinCode(
  groupId: string
): Promise<{ join_code: string }> {
  const res = await axiosInstance().post(`/api/groups/${groupId}/join-code`);
  return { join_code: res.data.code };
}

export async function disableJoinCode(groupId: string): Promise<void> {
  await axiosInstance().post(`/api/groups/${groupId}/join-code/disable`);
}

export async function listGroupStaff(
  groupId: string
): Promise<GroupStaffSummary[]> {
  const res = await axiosInstance().get(`/api/groups/${groupId}/staff`);
  return Array.isArray(res.data) ? res.data : [];
}

export async function removeGroupStaff(
  groupId: string,
  accountId: string
): Promise<void> {
  await axiosInstance().delete(`/api/groups/${groupId}/staff/${accountId}`);
}

export async function listGroupStaffInvitations(
  groupId: string
): Promise<GroupStaffInvitation[]> {
  const res = await axiosInstance().get<GroupStaffInvitation[]>(
    `/api/groups/${groupId}/staff/invitations`
  );
  return res.data;
}

export async function cancelGroupStaffInvitation(
  groupId: string,
  invitationId: string
): Promise<void> {
  await axiosInstance().delete(
    `/api/groups/${groupId}/staff/invitations/${invitationId}`
  );
}

export type UpdateGroupStaffRolePayload = {
  role: GroupStaffRole;
};

export type GroupStaffOut = {
  account_id: string;
  role: GroupStaffRole;
};
export async function updateGroupStaffRole(
  groupId: string,
  accountId: string,
  payload: UpdateGroupStaffRolePayload
): Promise<GroupStaffOut> {
  const res = await axiosInstance().patch(
    `/api/groups/${groupId}/staff/${accountId}`,
    payload
  );
  return res.data;
}

export type JoinGroupPayload = {
  code: string;
  profile_ids: string[];
  role: AccessLevel;
};
export type JoinGroupResponse = GroupMembersResponse & {
  group_name: string;
  org?: string | null;
};

export async function joinGroupByCode(
  payload: JoinGroupPayload
): Promise<JoinGroupResponse> {
  const res = await axiosInstance().post('/api/groups/join-by-code', payload, {
    timeout: 15_000,
  });
  return res.data;
}

export type JoinCodeStatus = {
  enabled: boolean;
  expired: boolean;
  expires_at?: string | null;
  code?: string | null;
};

export async function getJoinCodeStatus(groupId: string) {
  return axiosInstance().get<JoinCodeStatus>(
    `/api/groups/${groupId}/join-code`
  );
}
